import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, Method } from 'axios';
import config from '../config';
import { logout } from '../features/login/loginSlice';
import { store } from '../store';
import { getErrorMessage } from '../utils/errorHandler';

interface APIRawResponseSuccess {
  data: Record<string, unknown>[];
  message: null;
  status: 'ok';
}

interface APIRawResponseError {
  data: null;
  message: string;
  status: 'error';
  code?: string;
}

type APIRawResponse = APIRawResponseSuccess | APIRawResponseError;

interface APIResponseSuccess<T = Record<string, unknown>> {
  status: 'ok';
  data: T;
}

export interface APIResponseError {
  status: 'error';
  error: string;
  code?: string;
}

export type APIResponse<T = Record<string, unknown>> =
  | APIResponseSuccess<T>
  | APIResponseError;

export interface RawResponse<T = Record<string, unknown>> {
  status: string;
  message: string;
  data: T;
}

const api = async <T>(
  method: Method,
  route: string,
  data?: Record<string, any>
): Promise<APIResponse<T>> => {
  try {
    const options: AxiosRequestConfig = {
      method,
      withCredentials: false,
      headers: {
        'Content-Type': 'application/json',
      },
    };

    // Generamos el URL inicial con la ubicación de la api y los parámetros

    const url = new URL(`${config.api.url}/${route}`);

    if (data) {
      // Si el método no es PUT, POST o PATCH incluimos los datos en la URL
      if (!['PUT', 'POST', 'PATCH'].includes(method)) {
        Object.entries(data).forEach(entry => {
          let [key, value] = entry;

          if (typeof value === 'boolean') {
            // Convertimos los tipos booleanos a 0 o 1 respectivamente
            value = value === true ? '1' : '0';
          } else if (typeof value === 'object') {
            value = JSON.stringify(value);
          }

          // Agregamos el parámetro a la URL
          url.searchParams.set(key, value);
        });

        // Si vamos a hacer un PUT, POST o PATCH agregamos los datos al body
      } else {
        // Verificamos si hay archivos entre los datos a enviar
        const includesFile = Object.values(data).find(d => d instanceof File);

        // Si los datos incluyen un archivo convertimos el objeto en un FormData
        if (includesFile) {
          const formData = new FormData();

          for (const key in data) {
            // https://2ality.com/2012/01/objects-as-maps.html
            if (Object.prototype.hasOwnProperty.call(data, key)) {
              formData.append(key, data[key]);
            }
          }

          data = formData;
        }

        // Agregamos los datos a la petición
        options['data'] = data;
      }
    }

    // Calculamos la URL final
    options['url'] = url.toString();

    // Hacemos la petición
    const response: AxiosResponse<APIRawResponse> = await axios(options);

    // Errores de red / servidor
    if (response.status !== 200 && response.status !== 201) {
      if (response.data.status === 'error') {
        return {
          status: 'error',
          error: response.data.message
            ? response.data.message
            : response.statusText,
          code: response.data.code,
        };
      }
    }

    // Errores del servicio
    if (response.data.status !== 'ok') {
      return {
        status: 'error',
        error: response.data.message,
      };
    }

    return {
      status: 'ok',
      data: response.data.data as T,
    };
  } catch (err) {
    // Errores imprevistos
    if (axios.isAxiosError(err)) {
      const typedErr = err as AxiosError<APIRawResponseError>;

      if (typedErr.response && typedErr.response.data.status === 'error') {
        return {
          status: 'error',
          error: typedErr.response.data.message
            ? typedErr.response.data.message
            : typedErr.response.statusText,
          code: typedErr.response.data.code,
        };
      }
    }
    return {
      status: 'error',
      error: getErrorMessage(err),
    };
  }
};

function setTokenHeader(config: AxiosRequestConfig, token: string) {
  if (config.headers) {
    config.headers['Authorization'] = `Bearer ${token}`;
  } else {
    config.headers = {
      Authorization: `Bearer ${token}`,
    };
  }
  return config;
}

// Interceptamos las solicitudes para incluir el token
axios.interceptors.request.use(async config => {
  const currentToken = localStorage.getItem('jwt_token');
  if (!currentToken) {
    return config;
  }

  setTokenHeader(config, currentToken);

  return config;
});

// Interceptamos los errores en la respuesta para renovar el token
axios.interceptors.response.use(
  response => response,
  async err => {
    // Si el error no es de Axios o no es un error de token vencido o es un reintento dejamos seguir el error
    if (!axios.isAxiosError(err) || err.response?.status !== 401) {
      throw err;
    }
    await store.dispatch(logout());
  }
);

export default api;
